/** App Store id of the IsoFarm mobile app. */
const IOS_APP_ID = '6756572548';
/** Google Play package name of the IsoFarm mobile app. */
const ANDROID_PACKAGE_NAME = 'com.isofarmmobile';
/**
 * Public site of Tecomec Brasil S.A. (formerly Agres Sistemas Eletrônicos
 * S/A). agres.com.br redirects here, but the old per-language paths do not
 * exist on the new site, so links must point here directly.
 */
const COMPANY_SITE_URL = 'https://www.tecomec.com.br';

export class AppConstants {
  public static readonly LANGUAGES: {
    PT_BR: 'ptBR';
    EN: 'en';
    ES: 'es';
    IT: 'it';
  } = {
    PT_BR: 'ptBR',
    EN: 'en',
    ES: 'es',
    IT: 'it',
  };

  public static KEYS_LOCAL_STORAGE = {
    ISO_LANG: 'iso.lang',
    ISO_USUARIO: 'iso.usuario',
    ISO_ACCESS_TOKEN: 'iso.access_token',
    USER_LOCATION: 'userLocation',
    HIDE_MODAL: 'hide_modal',
    ROLE: 'role',

    DATE_EXPIRES: 'date_expires',

    REFRESH_TOKEN: 'refresh_token',
    FIRST_LOAD: 'firstLoad',
    UNITS: 'system_units',
    DEVICE_ID: 'deviceId',
    ID_TRATOR: 'idTrator',
    ISO_CONSENT: 'iso.consent',
    MOBILE_APP_PROMPT_DISMISSED: 'iso.mobile-app-prompt.dismissed',
  };

  /**
   * Prefix of localStorage keys that hold per-user work data, such as saved
   * calculator drafts. These keys are wiped on explicit logout and account
   * deletion by `AuthStateService.clearUserScopedData()`.
   */
  public static readonly USER_SCOPED_STORAGE_PREFIX = 'iso.user-scoped.';

  public static KEYS_SESSION_STORAGE = {
    PARAMETROS: 'parametros',
  };

  /** Destinations outside the app, always opened in a new tab. */
  public static readonly EXTERNAL_LINKS = {
    AGRES_ACADEMY: 'https://academy.tecomec.com.br/',
    /** Contact page of the company site, one URL per app language. */
    COMPANY_CONTACT: {
      ptBR: `${COMPANY_SITE_URL}/contato/`,
      en: `${COMPANY_SITE_URL}/en/contact/`,
      es: `${COMPANY_SITE_URL}/es/contacto/`,
      it: `${COMPANY_SITE_URL}/it/contatti/`,
    },
    /** "About us" page of the company site, one URL per app language. */
    COMPANY_ABOUT: {
      ptBR: `${COMPANY_SITE_URL}/quemsomos/`,
      en: `${COMPANY_SITE_URL}/en/about-us/`,
      es: `${COMPANY_SITE_URL}/es/quienes-somos/`,
      it: `${COMPANY_SITE_URL}/it/chi-siamo/`,
    },
    /** Digital agriculture page of the company site, one URL per app language. */
    COMPANY_DIGITAL_AGRICULTURE: {
      ptBR: `${COMPANY_SITE_URL}/agricultura_digital/`,
      en: `${COMPANY_SITE_URL}/en/digital-agriculture/`,
      es: `${COMPANY_SITE_URL}/es/agricultura-digital/`,
      it: `${COMPANY_SITE_URL}/it/agricoltura-digitale/`,
    },
  };

  /**
   * Store listings of the IsoFarm mobile app.
   *
   * @remarks `src/index.html` repeats the iOS app id in the `apple-itunes-app`
   * meta tag, which cannot read constants; keep both in sync.
   */
  public static readonly MOBILE_APP = {
    APP_STORE_URL: `https://apps.apple.com/br/app/isofarm/id${IOS_APP_ID}`,
    GOOGLE_PLAY_URL: `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE_NAME}`,
  };

  public static UNIT_SYSTEM_OPTIONS = {
    METRIC: 'metric',
    IMPERIAL: 'imperial',
  };

  public static UNIT_TEMPERATURE_OPTIONS = {
    CELSIUS: 'C',
    FAHRENHEIT: 'F',
  };

  public static UNIT_SPEED_OPTIONS = {
    KILOMETER_PER_HOUR: 'km/h',
    MILES_PER_HOUR: 'm/h',
    KILOMETER: 'km',
    MILES: 'm',
  };

  public static GRAPH_OPTIONS = {
    TEMPERATURE: 'temperature',
    RELATIVE_HUMIDITY: 'relative_humidity',
    WIND_SPEED: 'wind_speed',
    ROLLING_PRECIPITATION: 'rolling_precipitation',
  };

  public static RESULTADO_NAVEGACAO_OPTIONS = {
    NVG: 'NVG',
    TELEMETRIA: 'TELEMETRIA',
  };

  public static DEVICE_MODEL = {
    ISOVIEW: { name: 'isoview', extension: 'nvg' },
    SPRAYRATE: { name: 'spray-rate', extension: 'stk' },
    AGRONAVE: { name: 'agronave', extension: 'agp' },
    AGRONAVE_ENGINEERING: { name: 'agronave', extension: 'zip' },
    GEONAVE: { name: 'geonave', extension: 'nvg' },
    AGRONAVE12: { name: 'agronave12', extension: 'agp' },
    AGRONAVE12W: { name: 'agronave12w', extension: 'agp' },
    AGRONAVE7: { name: 'agronave7', extension: 'agp' },
  };

  /**
   * Device model values persisted by the backend with a spelling that differs from `DEVICE_MODEL` names.
   * The backend stores agroNave 12W as 'agronave12W' (capital W) in parsed tasks, task history and devices.
   */
  public static readonly DEVICE_MODEL_ALIASES: Readonly<
    Record<string, string>
  > = {
    agronave12W: 'agronave12w',
  };

  public static readonly COUNTRY_CODES = [
    'AFG',
    'ALB',
    'DZA',
    'ASM',
    'AND',
    'AGO',
    'AIA',
    'ATA',
    'ATG',
    'ARG',
    'ARM',
    'ABW',
    'AUS',
    'AUT',
    'AZE',
    'BHS',
    'BHR',
    'BGD',
    'BRB',
    'BLR',
    'BEL',
    'BLZ',
    'BEN',
    'BMU',
    'BTN',
    'BOL',
    'BES',
    'BIH',
    'BWA',
    'BVT',
    'BRA',
    'IOT',
    'BRN',
    'BGR',
    'BFA',
    'BDI',
    'CPV',
    'KHM',
    'CMR',
    'CAN',
    'CYM',
    'CAF',
    'TCD',
    'CHL',
    'CHN',
    'CXR',
    'CCK',
    'COL',
    'COM',
    'COD',
    'COG',
    'COK',
    'CRI',
    'HRV',
    'CUB',
    'CUW',
    'CYP',
    'CZE',
    'CIV',
    'DNK',
    'DJI',
    'DMA',
    'DOM',
    'ECU',
    'EGY',
    'SLV',
    'GNQ',
    'ERI',
    'EST',
    'SWZ',
    'ETH',
    'FLK',
    'FRO',
    'FJI',
    'FIN',
    'FRA',
    'GUF',
    'PYF',
    'ATF',
    'GAB',
    'GMB',
    'GEO',
    'DEU',
    'GHA',
    'GIB',
    'GRC',
    'GRL',
    'GRD',
    'GLP',
    'GUM',
    'GTM',
    'GGY',
    'GIN',
    'GNB',
    'GUY',
    'HTI',
    'HMD',
    'VAT',
    'HND',
    'HKG',
    'HUN',
    'ISL',
    'IND',
    'IDN',
    'IRN',
    'IRQ',
    'IRL',
    'IMN',
    'ISR',
    'ITA',
    'JAM',
    'JPN',
    'JEY',
    'JOR',
    'KAZ',
    'KEN',
    'KIR',
    'PRK',
    'KOR',
    'KWT',
    'KGZ',
    'LAO',
    'LVA',
    'LBN',
    'LSO',
    'LBR',
    'LBY',
    'LIE',
    'LTU',
    'LUX',
    'MAC',
    'MDG',
    'MWI',
    'MYS',
    'MDV',
    'MLI',
    'MLT',
    'MHL',
    'MTQ',
    'MRT',
    'MUS',
    'MYT',
    'MEX',
    'FSM',
    'MDA',
    'MCO',
    'MNG',
    'MNE',
    'MSR',
    'MAR',
    'MOZ',
    'MMR',
    'NAM',
    'NRU',
    'NPL',
    'NLD',
    'NCL',
    'NZL',
    'NIC',
    'NER',
    'NGA',
    'NIU',
    'NFK',
    'MNP',
    'NOR',
    'OMN',
    'PAK',
    'PLW',
    'PSE',
    'PAN',
    'PNG',
    'PRY',
    'PER',
    'PHL',
    'PCN',
    'POL',
    'PRT',
    'PRI',
    'QAT',
    'MKD',
    'ROU',
    'RUS',
    'RWA',
    'REU',
    'BLM',
    'SHN',
    'KNA',
    'LCA',
    'MAF',
    'SPM',
    'VCT',
    'WSM',
    'SMR',
    'STP',
    'SAU',
    'SEN',
    'SRB',
    'SYC',
    'SLE',
    'SGP',
    'SXM',
    'SVK',
    'SVN',
    'SLB',
    'SOM',
    'ZAF',
    'SGS',
    'SSD',
    'ESP',
    'LKA',
    'SDN',
    'SUR',
    'SJM',
    'SWE',
    'CHE',
    'SYR',
    'TWN',
    'TJK',
    'TZA',
    'THA',
    'TLS',
    'TGO',
    'TKL',
    'TON',
    'TTO',
    'TUN',
    'TUR',
    'TKM',
    'TCA',
    'TUV',
    'UGA',
    'UKR',
    'ARE',
    'GBR',
    'UMI',
    'USA',
    'URY',
    'UZB',
    'VUT',
    'VEN',
    'VNM',
    'VGB',
    'VIR',
    'WLF',
    'ESH',
    'YEM',
    'ZMB',
    'ZWE',
    'ALA',
  ];

  public static readonly COUNTRY_CODES_MAP: { [key: string]: string } = {
    AFG: 'AF',
    ALB: 'AL',
    DZA: 'DZ',
    ASM: 'AS',
    AND: 'AD',
    AGO: 'AO',
    AIA: 'AI',
    ATA: 'AQ',
    ATG: 'AG',
    ARG: 'AR',
    ARM: 'AM',
    ABW: 'AW',
    AUS: 'AU',
    AUT: 'AT',
    AZE: 'AZ',
    BHS: 'BS',
    BHR: 'BH',
    BGD: 'BD',
    BRB: 'BB',
    BLR: 'BY',
    BEL: 'BE',
    BLZ: 'BZ',
    BEN: 'BJ',
    BMU: 'BM',
    BTN: 'BT',
    BOL: 'BO',
    BES: 'BQ',
    BIH: 'BA',
    BWA: 'BW',
    BVT: 'BV',
    BRA: 'BR',
    IOT: 'IO',
    BRN: 'BN',
    BGR: 'BG',
    BFA: 'BF',
    BDI: 'BI',
    CPV: 'CV',
    KHM: 'KH',
    CMR: 'CM',
    CAN: 'CA',
    CYM: 'KY',
    CAF: 'CF',
    TCD: 'TD',
    CHL: 'CL',
    CHN: 'CN',
    CXR: 'CX',
    CCK: 'CC',
    COL: 'CO',
    COM: 'KM',
    COD: 'CD',
    COG: 'CG',
    COK: 'CK',
    CRI: 'CR',
    HRV: 'HR',
    CUB: 'CU',
    CUW: 'CW',
    CYP: 'CY',
    CZE: 'CZ',
    CIV: 'CI',
    DNK: 'DK',
    DJI: 'DJ',
    DMA: 'DM',
    DOM: 'DO',
    ECU: 'EC',
    EGY: 'EG',
    SLV: 'SV',
    GNQ: 'GQ',
    ERI: 'ER',
    EST: 'EE',
    SWZ: 'SZ',
    ETH: 'ET',
    FLK: 'FK',
    FRO: 'FO',
    FJI: 'FJ',
    FIN: 'FI',
    FRA: 'FR',
    GUF: 'GF',
    PYF: 'PF',
    ATF: 'TF',
    GAB: 'GA',
    GMB: 'GM',
    GEO: 'GE',
    DEU: 'DE',
    GHA: 'GH',
    GIB: 'GI',
    GRC: 'GR',
    GRL: 'GL',
    GRD: 'GD',
    GLP: 'GP',
    GUM: 'GU',
    GTM: 'GT',
    GGY: 'GG',
    GIN: 'GN',
    GNB: 'GW',
    GUY: 'GY',
    HTI: 'HT',
    HMD: 'HM',
    VAT: 'VA',
    HND: 'HN',
    HKG: 'HK',
    HUN: 'HU',
    ISL: 'IS',
    IND: 'IN',
    IDN: 'ID',
    IRN: 'IR',
    IRQ: 'IQ',
    IRL: 'IE',
    IMN: 'IM',
    ISR: 'IL',
    ITA: 'IT',
    JAM: 'JM',
    JPN: 'JP',
    JEY: 'JE',
    JOR: 'JO',
    KAZ: 'KZ',
    KEN: 'KE',
    KIR: 'KI',
    PRK: 'KP',
    KOR: 'KR',
    KWT: 'KW',
    KGZ: 'KG',
    LAO: 'LA',
    LVA: 'LV',
    LBN: 'LB',
    LSO: 'LS',
    LBR: 'LR',
    LBY: 'LY',
    LIE: 'LI',
    LTU: 'LT',
    LUX: 'LU',
    MAC: 'MO',
    MDG: 'MG',
    MWI: 'MW',
    MYS: 'MY',
    MDV: 'MV',
    MLI: 'ML',
    MLT: 'MT',
    MHL: 'MH',
    MTQ: 'MQ',
    MRT: 'MR',
    MUS: 'MU',
    MYT: 'YT',
    MEX: 'MX',
    FSM: 'FM',
    MDA: 'MD',
    MCO: 'MC',
    MNG: 'MN',
    MNE: 'ME',
    MSR: 'MS',
    MAR: 'MA',
    MOZ: 'MZ',
    MMR: 'MM',
    NAM: 'NA',
    NRU: 'NR',
    NPL: 'NP',
    NLD: 'NL',
    NCL: 'NC',
    NZL: 'NZ',
    NIC: 'NI',
    NER: 'NE',
    NGA: 'NG',
    NIU: 'NU',
    NFK: 'NF',
    MNP: 'MP',
    NOR: 'NO',
    OMN: 'OM',
    PAK: 'PK',
    PLW: 'PW',
    PSE: 'PS',
    PAN: 'PA',
    PNG: 'PG',
    PRY: 'PY',
    PER: 'PE',
    PHL: 'PH',
    PCN: 'PN',
    POL: 'PL',
    PRT: 'PT',
    PRI: 'PR',
    QAT: 'QA',
    MKD: 'MK',
    ROU: 'RO',
    RUS: 'RU',
    RWA: 'RW',
    REU: 'RE',
    BLM: 'BL',
    SHN: 'SH',
    KNA: 'KN',
    LCA: 'LC',
    MAF: 'MF',
    SPM: 'PM',
    VCT: 'VC',
    WSM: 'WS',
    SMR: 'SM',
    STP: 'ST',
    SAU: 'SA',
    SEN: 'SN',
    SRB: 'RS',
    SYC: 'SC',
    SLE: 'SL',
    SGP: 'SG',
    SXM: 'SX',
    SVK: 'SK',
    SVN: 'SI',
    SLB: 'SB',
    SOM: 'SO',
    ZAF: 'ZA',
    SGS: 'GS',
    SSD: 'SS',
    ESP: 'ES',
    LKA: 'LK',
    SDN: 'SD',
    SUR: 'SR',
    SJM: 'SJ',
    SWE: 'SE',
    CHE: 'CH',
    SYR: 'SY',
    TWN: 'TW',
    TJK: 'TJ',
    TZA: 'TZ',
    THA: 'TH',
    TLS: 'TL',
    TGO: 'TG',
    TKL: 'TK',
    TON: 'TO',
    TTO: 'TT',
    TUN: 'TN',
    TUR: 'TR',
    TKM: 'TM',
    TCA: 'TC',
    TUV: 'TV',
    UGA: 'UG',
    UKR: 'UA',
    ARE: 'AE',
    GBR: 'GB',
    UMI: 'UM',
    USA: 'US',
    URY: 'UY',
    UZB: 'UZ',
    VUT: 'VU',
    VEN: 'VE',
    VNM: 'VN',
    VGB: 'VG',
    VIR: 'VI',
    WLF: 'WF',
    ESH: 'EH',
    YEM: 'YE',
    ZMB: 'ZM',
    ZWE: 'ZW',
    ALA: 'AX',
  };

  // Section A - Applications
  public static readonly SECTION_A_APPLICATIONS_NAVIGATION =
    'experience-evolution.section-a-applications-navigation';
  public static readonly SECTION_A_APPLICATIONS_AUTOPILOT =
    'experience-evolution.section-a-applications-autopilot';
  public static readonly SECTION_A_APPLICATIONS_SPRAYING =
    'experience-evolution.section-a-applications-spraying';
  public static readonly SECTION_A_APPLICATIONS_FERTILIZATION =
    'experience-evolution.section-a-applications-fertilization';
  public static readonly SECTION_A_APPLICATIONS_PLANTING =
    'experience-evolution.section-a-applications-planting';
  public static readonly SECTION_A_APPLICATIONS_OTHERS =
    'experience-evolution.section-a-applications-others';

  // Section A - Products
  public static readonly SECTION_A_PRODUCTS_STRONGBOX =
    'experience-evolution.section-a-products-strongbox';
  public static readonly SECTION_A_PRODUCTS_SPRAYRATEMINI =
    'experience-evolution.section-a-products-sprayratemini';
  public static readonly SECTION_A_PRODUCTS_SPRAYRATE =
    'experience-evolution.section-a-products-sprayrate';
  public static readonly SECTION_A_PRODUCTS_ISOVIEW =
    'experience-evolution.section-a-products-isoview';
  public static readonly SECTION_A_PRODUCTS_GEONAVE =
    'experience-evolution.section-a-products-geonave';
  public static readonly SECTION_A_PRODUCTS_AGRONAVE =
    'experience-evolution.section-a-products-agronave';
  public static readonly SECTION_A_PRODUCTS_COMPONENTS =
    'experience-evolution.section-a-products-components';
  public static readonly SECTION_A_PRODUCTS_DONT_KNOW =
    'experience-evolution.section-a-products-dont-know';
  public static readonly SECTION_A_PRODUCTS_OTHERS =
    'experience-evolution.section-a-products-others';

  // Section G - Frequency
  public static readonly SECTION_G_FREQUENCY_DAILY =
    'experience-evolution.section-g-frequency-daily';
  public static readonly SECTION_G_FREQUENCY_WEEKLY =
    'experience-evolution.section-g-frequency-weekly';
  public static readonly SECTION_G_FREQUENCY_SEVERAL_TIMES =
    'experience-evolution.section-g-frequency-several-times';
  public static readonly SECTION_G_FREQUENCY_SEASONAL =
    'experience-evolution.section-g-frequency-seasonal';
  public static readonly SECTION_G_FREQUENCY_RARELY =
    'experience-evolution.section-g-frequency-rarely';
  public static readonly SECTION_G_FREQUENCY_NEVER =
    'experience-evolution.section-g-frequency-never';
  public static readonly SECTION_G_FREQUENCY_OTHERS =
    'experience-evolution.section-g-frequency-others';

  // Section G - Access Method
  public static readonly SECTION_G_ACCESS_MOBILE =
    'experience-evolution.section-g-access-mobile';
  public static readonly SECTION_G_ACCESS_COMPUTER =
    'experience-evolution.section-g-access-computer';
  public static readonly SECTION_G_ACCESS_BOTH =
    'experience-evolution.section-g-access-both';

  // Section G - Features
  public static readonly SECTION_G_FEATURES_MAPS =
    'experience-evolution.section-g-features-maps';
  public static readonly SECTION_G_FEATURES_REPORTS =
    'experience-evolution.section-g-features-reports';
  public static readonly SECTION_G_FEATURES_MANAGEMENT =
    'experience-evolution.section-g-features-management';
  public static readonly SECTION_G_FEATURES_PLANNING =
    'experience-evolution.section-g-features-planning';
  public static readonly SECTION_G_FEATURES_SHARING =
    'experience-evolution.section-g-features-sharing';
  public static readonly SECTION_G_FEATURES_ALERTS =
    'experience-evolution.section-g-features-alerts';
  public static readonly SECTION_G_FEATURES_HAVE_USED =
    'experience-evolution.section-g-features-have-used';

  public static readonly SECTION_G_FEATURES_OTHER =
    'experience-evolution.section-g-features-other';

  // Section E - Data Download Methods
  public static readonly SECTION_E_METHOD_PENDRIVE =
    'experience-evolution.section-e-method-pendrive';
  public static readonly SECTION_E_METHOD_TELEMETRY =
    'experience-evolution.section-e-method-telemetry';
  public static readonly SECTION_E_METHOD_NEVER_DOWNLOADED =
    'experience-evolution.section-e-method-never-downloaded';

  // Section E - Yes/No
  public static readonly SECTION_E_YES = 'experience-evolution.section-e-yes';
  public static readonly SECTION_E_NO = 'experience-evolution.section-e-no';

  // Section E - Wants Advanced Features
  public static readonly SECTION_E_YES_ADVANCE =
    'experience-evolution.section-e-yes-advance';
  public static readonly SECTION_E_NO_ADVANCE =
    'experience-evolution.section-e-no-advance';

  // Section E - Missing Features
  public static readonly SECTION_E_Q15_PANEL =
    'experience-evolution.section-e-question-15-panel';
  public static readonly SECTION_E_Q15_METRICS =
    'experience-evolution.section-e-question-15-metrics';
  public static readonly SECTION_E_Q15_ALERT =
    'experience-evolution.section-e-question-15-alert';
  public static readonly SECTION_E_Q15_INTEGRATION =
    'experience-evolution.section-e-question-15-integration';
  public static readonly SECTION_E_Q15_WEATHER =
    'experience-evolution.section-e-question-15-weather';
  public static readonly SECTION_E_Q15_POSSIBILITY =
    'experience-evolution.section-e-question-15-possibility';
  public static readonly SECTION_E_Q15_READ_INFORMATION =
    'experience-evolution.section-e-question-15-read-information';
  public static readonly SECTION_E_Q15_OTHERS =
    'experience-evolution.section-e-question-15-others';
  public static readonly SECTION_E_FEATURES_OTHERS =
    'experience-evolution.section-e-features-others';

  // Section E - Platform Integration
  public static readonly SECTION_E_PLATFORM_NO =
    'experience-evolution.section-e-no';
  public static readonly SECTION_E_PLATFORM_YES =
    'experience-evolution.section-e-yes';

  // Section E - MOBILE APP
  public static readonly SECTION_E_A_APP_MOBILE_14 =
    'experience-evolution.section-e-a-app-mobile-14';
  public static readonly SECTION_E_A_APP_MOBILE_14_YES =
    'experience-evolution.section-e-a-app-mobile-14-yes';
  public static readonly SECTION_E_A_APP_MOBILE_14_NO =
    'experience-evolution.section-e-a-app-mobile-14-no';
  public static readonly SECTION_E_A_APP_MOBILE_14_MAYBE =
    'experience-evolution.section-e-a-app-mobile-14-maybe';

  // Section S - Training
  public static readonly SECTION_S_TRAINING_DIGITAL_MANUAL =
    'experience-evolution.section-s-training-digital-manual';
  public static readonly SECTION_S_TRAINING_VIDEO =
    'experience-evolution.section-s-training-video';
  public static readonly SECTION_S_TRAINING_REMOTE =
    'experience-evolution.section-s-training-remote-class';
  public static readonly SECTION_S_TRAINING_NOT_NEEDED =
    'experience-evolution.section-s-training-not-needed';
}
